import { useQueryClient } from "@tanstack/react-query";
import { useGetIdentity, useTranslate } from "ra-core";
import { useSearchParams } from "react-router";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { CrmEvent, DealCard, DealStage } from "../types";
import { getCardStatus, type CardTone } from "./cardStatus";

/**
 * Funnel screens write with the data provider directly, which does not
 * invalidate react-admin's cache: refresh every resource a card is built from.
 */
export const useRefreshFunnel = () => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all(
      [
        "deal_cards",
        "task_cards",
        "deals",
        "contacts",
        "tasks",
        "calls",
        "events",
        "apartments",
      ].map((resource) =>
        queryClient.invalidateQueries({ queryKey: [resource] }),
      ),
    );
};

/** Stages shown as funnel columns: everything but "lost" */
export const useFunnelStages = () => {
  const { dealStages } = useConfigurationContext();
  return dealStages.filter((stage) => stage.value !== "lost");
};

export const useStage = () => {
  const { dealStages } = useConfigurationContext();
  return (value: string | null | undefined): DealStage | undefined =>
    dealStages.find((stage) => stage.value === value);
};

export const usePermissions = (): string[] => {
  const { identity } = useGetIdentity();
  return identity?.permissions ?? [];
};

export const toneClass: Record<CardTone, string> = {
  danger: "text-destructive",
  warning: "text-warning",
  ok: "text-primary",
  muted: "text-muted-foreground",
};

/** The status line of a funnel card, translated */
export const useCardStatusText = () => {
  const translate = useTranslate();
  const { taskTypes } = useConfigurationContext();
  return (card: DealCard, now: Date) => {
    const status = getCardStatus(card, now);
    if (!status) return null;
    const values: Record<string, string | number> = { ...status.values };
    if (typeof values.unit === "string") {
      values.unit = translate(`crm.funnel.units.${values.unit}`);
    }
    if (typeof values.task === "string") {
      values.task =
        taskTypes.find((type) => type.value === values.task)?.label ??
        values.task;
    }
    return {
      text: translate(`crm.funnel.status.${status.key}`, values),
      tone: status.tone,
    };
  };
};

export const fullName = (card: {
  first_name?: string | null;
  last_name?: string | null;
  name?: string;
}) =>
  [card.first_name, card.last_name].filter(Boolean).join(" ") ||
  card.name ||
  "";

/** "Кв. 23 · Facebook" or "2-кімн. · Instagram · Квартири Aqua Vita" (the form) */
export const useCardSubtitle = () => {
  const translate = useTranslate();
  return (card: DealCard) =>
    [
      card.apartment_number
        ? translate("crm.funnel.apartment", { number: card.apartment_number })
        : card.rooms_wanted
          ? translate("crm.funnel.rooms", { count: card.rooms_wanted })
          : null,
      card.source
        ? translate(`resources.contacts.sources.${card.source}`)
        : null,
      card.source_form ?? null,
    ]
      .filter(Boolean)
      .join(" · ");
};

/** Human text for an event of the log (payloads hold ids and codes only) */
export const useEventText = () => {
  const translate = useTranslate();
  const stage = useStage();
  const { dealLostReasons, taskTypes } = useConfigurationContext();
  return (event: CrmEvent) => {
    const payload = event.payload as Record<string, string | null>;
    switch (event.type) {
      case "lead.created":
        return payload.source
          ? translate("crm.funnel.events.lead_created_from", {
              source: translate(`resources.contacts.sources.${payload.source}`),
            })
          : translate("crm.funnel.events.lead_created");
      case "lead.assigned":
        return translate("crm.funnel.events.lead_assigned");
      case "deal.created":
        return translate("crm.funnel.events.deal_created");
      case "deal.stage_changed":
        return translate("crm.funnel.events.deal_stage_changed", {
          from: stage(payload.from)?.label ?? payload.from,
          to: stage(payload.to)?.label ?? payload.to,
        });
      case "deal.lost":
        return translate("crm.funnel.events.deal_lost", {
          reason:
            dealLostReasons.find((r) => r.value === payload.lost_reason)
              ?.label ??
            payload.lost_reason ??
            "",
        });
      case "deal.assigned":
        return translate("crm.funnel.events.deal_assigned");
      case "deal.apartment_changed":
        return translate("crm.funnel.events.deal_apartment_changed");
      case "call.logged":
        return translate("crm.funnel.events.call_logged", {
          result: translate(`resources.calls.results.${payload.result}`),
        });
      case "task.created":
        return translate("crm.funnel.events.task_created", {
          task:
            taskTypes.find((type) => type.value === payload.type)?.label ??
            payload.type ??
            "",
        });
      case "task.done":
        return translate("crm.funnel.events.task_done");
      default:
        return translate("crm.funnel.events.other");
    }
  };
};

/** Funnel filters shared by both segments: manager and source */
export const useFunnelFilters = () => {
  const [params, setParams] = useSearchParams();
  const salesId = params.get("manager") ?? "";
  const source = params.get("source") ?? "";
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const filter: Record<string, unknown> = { "archived_at@is": null };
  if (salesId) filter.sales_id = Number(salesId);
  if (source) filter.source = source;
  return { salesId, source, set, filter, params };
};
